<template>
  <hr-branded-form-page
    @submit="onLogin"
  >
    <div class="row q-col-gutter-sm">
      <hr-text-field
        v-model="loginData.email"
        type="email"
        :label="$t('login.inputEmailLabel')"
        :rules="loginEmailRules"
        autocomplete="username"
        data-e2e="input-email"
        class="col-12"
      />
      <hr-password-field
        v-model="loginData.password"
        :label="$t('login.inputPasswordLabel')"
        autocomplete="current-password"
        data-e2e="input-password"
        class="col-12"
      />
    </div>
    <div class="row no-wrap items-center">
      <hr-checkbox
        v-model="loginData.rememberEmail"
        :label="$t('login.checkboxRememberEmailLabel')"
        data-e2e="checkbox-remember-email"
      />
      <hr-space />
      <hr-subtle-btn
        :label="$t('login.buttonForgotPassword')"
        data-e2e="button-forgot-password"
        @click="onShowForgotPassword"
      />
    </div>
    <template #actions>
      <hr-page-action-btn
        type="submit"
        :label="$t('login.buttonLogin')"
        :loading="loginSpinner"
        data-e2e="button-login"
      />
      <div class="row items-center">
        <hr-divider class="col" />
        <span class="col-auto q-mx-md text-grey-6">{{ orText }}</span>
        <hr-divider class="col" />
      </div>
      <provider-authorization-buttons
        :display-google="hasGoogleButton"
        :display-apple="hasAppleButton"
        :is-google-loading="googleAuthSpinner"
        :is-apple-loading="appleAuthSpinner"
        :is-microsoft-loading="microsoftAuthSpinner"
        @google-click="onGoogleLogin"
        @apple-click="onAppleLogin"
        @microsoft-click="onMicrosoftLogin(loginData.email)"
      />
      <hr-divider v-if="!isCapacitorMode && $route.query.signupPath" />
    </template>
    <template #footer>
      <span data-e2e="text-current-region">{{ currentRegionLabel }}</span>
      <span>Version: {{ version }}</span>
    </template>
  </hr-branded-form-page>
</template>

<script>
import { auth, callFirebaseFunction } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import ProviderAuthorizationButtons from 'components/UI/buttons/ProviderAuthorizationButtons';
import VerifyAuthenticatorAppMfa from 'components/Users/Modals/VerifyAuthenticatorAppMfa.vue';
import VerifyPhoneNumberMfa from 'components/Users/Modals/VerifyPhoneNumberMfa.vue';
import { getMultiFactorResolver } from 'firebase/auth';
import {
  capitalize, chain, get, has, isEmpty, omit,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import { AUTH_ERROR_CODE, MFA_METHODS } from 'src/constants/authorization';
import {
  HrBrandedFormPage,
  HrCheckbox,
  HrDivider,
  HrPageActionBtn,
  HrPasswordField,
  HrSpace,
  HrSubtleBtn,
  HrTextField,
  promptDialog,
} from 'src/design-system';
import UsersDB from 'src/fb/users-db';
import SsoProvidersMixin from 'src/mixins/SsoProvidersMixin';
import { auth as authStore } from 'src/store/auth';
import { region } from 'src/store/region';
import { isValidEmail } from 'src/utils/AppUtils';
import { clearBackups } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';
import { useTime } from 'src/composables/time';

const LOGIN_ERROR_MESSAGES = {
  [AUTH_ERROR_CODE.EXISTS_WITH_DIFFERENT_CREDENTIAL]: 'login.notifyAccountExistsWithDifferentCredential',
  [AUTH_ERROR_CODE.MISSING_PASSWORD]: 'login.inputPasswordValidator',
  [AUTH_ERROR_CODE.USER_DISABLED]: 'login.notifyUserDisabled',
  [AUTH_ERROR_CODE.USER_NOT_FOUND]: 'login.notifyWrongPasswordError',
  [AUTH_ERROR_CODE.WRONG_PASSWORD]: 'login.notifyWrongPasswordError',
};

export default {
  name: 'Login',

  components: {
    HrBrandedFormPage,
    HrCheckbox,
    HrPageActionBtn,
    HrDivider,
    HrPasswordField,
    HrSpace,
    HrSubtleBtn,
    HrTextField,
    ProviderAuthorizationButtons,
  },

  mixins: [SsoProvidersMixin],

  // Provided by the `useMobileMicrosoftAuth` composable from `App.vue` if we are in the Capacitor mode
  inject: {
    authToken: {
      default: null,
    },

    authError: {
      default: null,
    },
  },

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      await vm.checkRegionWithTimeout();

      if (!isEmpty(vm.userDetails)) {
        // When a hard refresh on a protected route bounces here before auth is restored, the
        // guard leaves a `redirect` query. Don't navigate in that case — leave the ?redirect in
        // the URL so handleAuthStateChanged's doRedirect can send the user back to it. Pushing
        // here (even to the redirect target) would drop the query and land them on home.
        if (!vm.$route.query.redirect) {
          vm.$router.push('/');
        }
      }

      await vm.microsoftLoginWithQueryData(to.query);
    });
  },

  beforeRouteLeave(to, from, next) {
    next((vm) => {
      if (vm.timeoutId) {
        clearTimeout(vm.timeoutId);
      }
    });
  },

  setup() {
    const { getLocaleDateString } = useTime();
    return { getLocaleDateString };
  },

  data() {
    return {
      loginSpinner: false,

      loginEmailRules: [
        (val) => (!chain(val).trim().isEmpty().value())
          || this.$t('login.inputNoEmailValidator'),
        (val) => isValidEmail(val) || this.$t('login.inputEmailValidator'),
      ],

      loginData: {
        email: '',
        password: '',
        rememberEmail: false,
      },

      timeoutId: null,
    };
  },

  computed: {
    ...mapState(authStore, ['userDetails', 'ssoProviders']),
    ...mapState(region, ['region', 'currentRegionLabel', 'regionLabel']),

    version() {
      return process.env.version;
    },

    orText() {
      return capitalize(this.$t('common.or'));
    },

    hasSSOButtons() {
      return this.hasGoogleButton || this.hasAppleButton;
    },

    hasGoogleButton() {
      return this.isCapacitorMode ? true : !!(this.gglProvider);
    },

    hasAppleButton() {
      return this.isCapacitorMode ? true : !!(this.appleProvider);
    },

    signupPath() {
      return get(this.$route.query, 'signupPath', '/signup');
    },
  },

  watch: {
    authToken: {
      handler(token) {
        if (token && isEmpty(this.userDetails)) {
          this.microsoftLoginWithQueryData({ token });
        }
      },
    },

    authError: {
      handler(errMessage) {
        if (errMessage && isEmpty(this.userDetails)) {
          this.microsoftLoginWithQueryData({ authError: errMessage });
        }
      },
    },
  },

  created() {
    // @todo Experimental feature: enable persistance as an option only
    if (has(this.$route.query, 'enablePersistance')) {
      localStorage.setItem('forceEnablePersistance', true);
    } else if (has(this.$route.query, 'disablePersistance')) {
      localStorage.removeItem('forceEnablePersistance');
    }

    if (this.$route.query.email) {
      this.loginData.email = this.$route.query.email;
    }

    const emailFromLocalStorage = localStorage.getItem('remember_email');

    if (emailFromLocalStorage) {
      this.loginData.email = emailFromLocalStorage;
      this.loginData.rememberEmail = true;
    }
  },

  methods: {
    isValidEmail,
    ...mapActions(authStore, ['login', 'sendResetPasswordLink']),
    ...mapActions(region, ['setRegion']),

    async checkRegionWithTimeout() {
      const TIMEOUT_MS = 4000;
      try {
        await Promise.race([
          this.setRegionFromIp(),
          this.timeout(TIMEOUT_MS).then(() => {
            throw new Error('time-out');
          }),
        ]);
      } catch (error) {
        if (error.message === 'time-out') {
          console.warn('setRegionFromIp took too long and was skipped');
        } else {
          logEvent(error);
        }
      }
    }, // checkRegionWithTimeout()

    timeout(ms) {
      return new Promise((resolve) => {
        this.timeoutId = setTimeout(() => {
          return resolve();
        }, ms);
      });
    }, // timeout()

    async setRegionFromIp() {
      // Check if selected_region local storage is set
      // otherwise, set it before proceeding.
      if (!localStorage.getItem('selected_region')) {
        try {
          const response = await fetch('https://api.ipify.org?format=json');

          const { ip } = await response.json();
          const { data } = await callFirebaseFunction('getRegionByIp', { ip });

          // Re-check after async operations — user may have manually chosen a region
          // while the IP lookup was in progress.
          if (!localStorage.getItem('selected_region')) {
            await this.setRegion({
              region: data.region || 'us',
              context: this,
            });
          }
        } catch (error) {
          logEvent(error);
        }
      }
    }, // setRegionFromIp()

    async onLogin() {
      // return when login is in progress
      if (this.loginSpinner) {
        return;
      }

      this.loginSpinner = true;

      try {
        await this.login(this.loginData);
        await this.setRegion({
          region: this.region,
          context: this,
        });
      } catch (loginError) {
        if ([
          AUTH_ERROR_CODE.USER_NOT_FOUND,
          AUTH_ERROR_CODE.USER_DISABLED,
        ].includes(loginError.code)) {
          try {
            const { data } = await callFirebaseFunction('findUser', {
              email: this.loginData.email,
            });

            // user found
            if (data) {
              await this.setRegion({
                region: data.region,
                context: this,
              });

              this.loginSpinner = false;
              await this.onLogin();
            } else {
              const deletedUserDate = await callFirebaseFunction('findDeletedUser', {
                email: this.loginData.email,
              });
              const deletedTimestamp = get(deletedUserDate, ['data', 'deleteTimestamp']);
              const deletedDate = this.getLocaleDateString(new Date(deletedTimestamp));

              const messageKey = LOGIN_ERROR_MESSAGES[loginError.code];
              const fallbackMessage = messageKey ? this.$t(messageKey) : loginError.message;
              const errorMessage = deletedTimestamp
                ? this.$t('login.notifyUserWasDeleted', { deletedDate })
                : fallbackMessage;
              notifyNegative(errorMessage);
              this.loginSpinner = false;
            }
          } catch (findUserError) {
            this.loginSpinner = false;
            logEvent(findUserError);
          }
        } else if (loginError.code === AUTH_ERROR_CODE.MULTI_FACTOR_REQUIRED) {
          this.showVerifyMFADialog(loginError);
        } else if (loginError.code === AUTH_ERROR_CODE.NETWORK_REQUEST_FAILED) {
          notifyNegative(this.$t('common.noInternetConnectionMessage'));
        } else {
          this.loginSpinner = false;
          const messageKey = LOGIN_ERROR_MESSAGES[loginError.code];
          const errorMessage = messageKey ? this.$t(messageKey) : loginError.message;
          notifyNegative(errorMessage);
        }
      } finally {
        this.loginSpinner = false;
        await clearBackups();
      }
    }, // onLogin()

    async microsoftLoginWithQueryData(pageQuery = {}) {
      const { token, authError } = (pageQuery || {});
      if (!token && !authError) {
        return;
      }

      // We should remove the token from the history state
      this.$router.replace({ name: 'login', query: omit(pageQuery, ['authError', 'token']) });

      if (authError) {
        notifyNegative(this.$t('login.notifyMicrosoftAuthError', { message: authError }));
        return;
      }

      if (token) {
        // Our custom Microsoft auth provider redirected user to the login page along with the auth token in query,
        // so we need to log in using it
        this.microsoftAuthSpinner = true;
        try {
          await this.loginWithCustomToken(token);
        } catch (err) {
          console.error(err);
        } finally {
          this.microsoftAuthSpinner = false;
        }
      }
    }, // microsoftLoginWithQueryData()

    onShowForgotPassword() {
      const initialEmail = this.loginData.email.toLowerCase().trim();
      promptDialog(
        this.$t('login.textForgotPasswordTitle'),
        this.$t('login.textForgotPasswordSubtitle'),
        (email) => this.onSendResetLink(email),
        {
          model: initialEmail,
          label: this.$t('login.inputResetEmailLabel'),
          type: 'email',
          rules: [(val) => isValidEmail(val) || this.$t('login.inputEmailValidator')],
          lazyRules: true,
          confirmLabel: this.$t('login.buttonSendResetLink'),
        },
      );
    }, // onShowForgotPassword()

    async onSendResetLink(email) {
      try {
        await this.sendResetPasswordLink({ email });
        notifySuccess(this.$t('login.notifySendResetLinkSuccess'));
      } catch (error) {
        if (error.code === AUTH_ERROR_CODE.NETWORK_REQUEST_FAILED) {
          notifyNegative(this.$t('common.noInternetConnectionMessage'));
        } else if (error.code === AUTH_ERROR_CODE.USER_NOT_FOUND) {
          const { data } = await callFirebaseFunction('findUser', { email });

          if (data) {
            await this.setRegion({ region: data.region, context: this });
            await this.sendResetPasswordLink({ email });
          }

          // Always show success — the message is intentionally non-committal
          // ("if registered, we'll send an email") to avoid email enumeration.
          notifySuccess(this.$t('login.notifySendResetLinkSuccess'));
        } else {
          notifyNegative(this.$t('common.errors.generic'));
        }
      }
    }, // onSendResetLink()

    showVerifyMFADialog(error) {
      const resolver = getMultiFactorResolver(auth, error);
      const factorId = get(resolver, ['hints', 0, 'factorId']);
      const MfaComponent = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? VerifyAuthenticatorAppMfa
        : VerifyPhoneNumberMfa;
      const mfaProps = factorId === MFA_METHODS.AUTHENTICATOR_APP.ID
        ? { resolver }
        : { resolver, loginData: this.loginData };

      this.openComponentDialog(
        MfaComponent,
        mfaProps,
        async () => {
          localStorage.setItem('userAuthenticated', 'true');

          const usersDB = new UsersDB();
          const docSnapshot = await usersDB.getDocSnapshot(auth.currentUser.uid);

          if (!docSnapshot.exists()) {
            notifyNegative(i18n.t('store.reloadUserDetailsError'));
            throw new Error(i18n.t('store.reloadUserDetailsError'));
          }
        },
        async () => {
          this.loginSpinner = false;
        },
      );
    }, // showVerifyMFADialog()

  },

};
</script>
